<template>
  <div class="patient-search-container">
    <!-- Search Form -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden mb-6">
      <ComponentHeader title="Patient Search" />
      <form @submit.prevent="searchPatients" class="p-6 space-y-4">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Patient Identifier (MBI)</label>
            <input v-model="searchFilters.MBI" type="text" class="sensitive search-input" placeholder="Enter MBI ID" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
            <input v-model="searchFilters.LAST_NAME" type="text" class="sensitive search-input" placeholder="Enter last name" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">First Name</label>
            <input v-model="searchFilters.FIRST_NAME" type="text" class="sensitive search-input" placeholder="Enter first name" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
            <input v-model="searchFilters.DATE_OF_BIRTH" type="date" class="sensitive search-input" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Gender</label>
            <select v-model="searchFilters.GENDER" class="search-input">
              <option value="">Select Gender</option>
              <option value="1">Male</option>
              <option value="2">Female</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
            <input v-model="searchFilters.BENE_ZIP_CD" type="text" class="search-input" placeholder="Enter ZIP code" />
          </div>
        </div>

        <div class="pt-4">
          <div class="flex gap-2">
            <button type="submit" :disabled="loading" class="btn-primary">
              <span v-if="!loading" class="flex items-center gap-2">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                </svg>
                Search
              </span>
              <span v-else class="flex items-center gap-2">
                <svg class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Searching...
              </span>
            </button>
            <button type="button" @click="clearSearch" class="btn-secondary">Clear</button>
          </div>
          <div v-if="validationError" class="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
            <span class="text-sm text-red-800 font-medium">{{ validationError }}</span>
          </div>
        </div>
      </form>
    </div>

    <!-- Results -->
    <div v-if="hasSearched" class="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
      <ComponentHeader :title="resultsTitle" />

      <div v-if="loading" class="p-12">
        <LoadingIndicator message="Searching for patients..." />
      </div>

      <div v-else-if="patients.length > 0" class="p-4">
        <DataTable
          :columns="columns"
          :rows="patients"
          row-key="MBI"
          max-height="60vh"
          export-title="Patient Search"
          export-filename="patient_search"
          @row-click="goToPatient"
        />
      </div>

      <div v-else class="p-12 text-center">
        <p class="text-gray-600">No patients found matching your search criteria.</p>
        <p class="text-sm text-gray-500 mt-2">Try adjusting your search parameters.</p>
      </div>
    </div>

    <div v-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
      <span class="text-red-800">{{ error }}</span>
    </div>
  </div>
</template>

<script>
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue'
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue'
import { sensitiveCell } from '@/utils/demoSpoof'
import { DataTable } from '@/components/External/table'

export default {
  name: 'ExternalPatientSearch',
  components: { ComponentHeader, LoadingIndicator, DataTable },
  emits: ['select'],
  data() {
    return {
      searchFilters: {
        MBI: '',
        LAST_NAME: '',
        FIRST_NAME: '',
        DATE_OF_BIRTH: '',
        GENDER: '',
        BENE_ZIP_CD: '',
      },
      patients: [],
      totalResults: 0,
      searchMs: 0,
      loading: false,
      hasSearched: false,
      error: null,
      validationError: null,
      columns: [
        { field: 'LAST_NAME', header: 'Last Name', type: 'text', sticky: true, filterable: true, cellClass: () => sensitiveCell('person-last') },
        { field: 'FIRST_NAME', header: 'First Name', type: 'text', filterable: true, cellClass: () => sensitiveCell('person-first') },
        { field: 'MBI', header: 'MBI', type: 'text', filterable: true, cellClass: () => sensitiveCell('identifier') },
        { field: 'DATE_OF_BIRTH', header: 'DOB', type: 'date', cellClass: () => sensitiveCell('dob') },
        { field: 'GENDER', header: 'Gender', type: 'text', format: v => this.formatGender(v) },
        { field: 'BENE_AGE', header: 'Age', type: 'number', format: v => parseInt(v) || '' },
        { field: 'BENE_ZIP_CD', header: 'ZIP', type: 'text', filterable: true },
        { field: 'BENE_FIPS_STATE_CD', header: 'State', type: 'text' },
        { field: 'ACO_ID', header: 'ACO', type: 'text', filterable: true },
        { field: 'PHONE_PRIMARY', header: 'Phone', type: 'text', cellClass: () => 'sensitive' },
        { field: 'EMAIL', header: 'Email', type: 'text', cellClass: () => 'sensitive' },
      ],
    }
  },
  computed: {
    resultsTitle() {
      if (this.patients.length > 0) {
        const shown = this.patients.length
        const total = this.totalResults
        const ms = this.searchMs
        if (total > shown) return `Showing ${shown} of ${total.toLocaleString()} patients (${ms}ms)`
        return `${shown} patient${shown !== 1 ? 's' : ''} found (${ms}ms)`
      }
      return 'Search Results'
    },
  },
  methods: {
    goToPatient(p) {
      this.$router.push({ name: 'ExternalPatientDetail', params: { patientIdentifier: p.MBI } })
    },
    validateSearch() {
      const f = this.searchFilters
      const hasAny = Object.values(f).some(v => v && v.trim())
      if (!hasAny) {
        this.validationError = 'Please enter at least one search criterion'
        return false
      }
      if ((f.GENDER || f.DATE_OF_BIRTH) && !f.MBI && !f.LAST_NAME && !f.FIRST_NAME && !f.BENE_ZIP_CD) {
        this.validationError = 'Please provide additional search criteria beyond gender or date of birth'
        return false
      }
      this.validationError = null
      return true
    },
    async searchPatients() {
      if (!this.validateSearch()) return
      this.loading = true
      this.error = null
      this.hasSearched = true

      const params = { type: 'PATIENT', limit: 100 }
      for (const [key, value] of Object.entries(this.searchFilters)) {
        if (value && value.trim()) params[key] = value.trim()
      }

      try {
        const { data } = await this.$axios.get('/api/graph/search/', { params })
        this.patients = data.results || []
        this.totalResults = data.total || 0
        this.searchMs = data.ms || 0
      } catch (err) {
        this.error = err.response?.data?.error || err.response?.data?.detail || 'Search failed.'
        this.patients = []
      } finally {
        this.loading = false
      }
    },
    clearSearch() {
      this.searchFilters = { MBI: '', LAST_NAME: '', FIRST_NAME: '', DATE_OF_BIRTH: '', GENDER: '', BENE_ZIP_CD: '' }
      this.patients = []
      this.totalResults = 0
      this.searchMs = 0
      this.hasSearched = false
      this.error = null
      this.validationError = null
    },
    formatGender(val) {
      return { '1': 'Male', '2': 'Female' }[val] || val || ''
    },
  },
}
</script>

<style scoped>
.patient-search-container {
  max-width: 1400px;
  margin: 0 auto;
}
.search-input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
}
.search-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
}
</style>
